import * as React from "react";
import {useAuth} from "../context/AuthContext";
import {ALL_BRANCHES_VALUE, usePeriod} from "../context/PeriodContext";
import {API_BASE_URL} from "../config/apiConfig";
import type {PayrollCalculation} from "./usePayrollRun";
import type {BankAccount} from "../types/contract";
import {
    BankExportError,
    exportAtkReport as exportAtkReportRequest,
    exportBankList as exportBankListRequest,
    fetchSupportedBankExports,
    PayrollWorkflowConflictError,
    type SupportedBankExport,
} from "../services/payrollService";

// ─── Types ──────────────────────────────────────────────────────────────────

interface PayrollRunApiResponse {
    totalRecords: number;
    payrollCalculations: PayrollCalculation[];
    totalGross: number;
    totalNetPay: number;
}

export interface ReportsBeneficiaryRow {
    id: string;
    name: string;
    branch: string;
    netAmount: number;
    /** The single active bank account of the employee; null when none (normal for contracts not yet edited). */
    bankAccount: BankAccount | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** "8-2026"/"2026-8" -> "08-2026" or "08/2026" depending on `separator`. */
export function formatPeriodNumeric(periodKey: string | null, separator: "-" | "/"): string {
    if (!periodKey) return "";
    const parts = periodKey.split("-");
    if (parts.length < 2) return periodKey;
    let month: number;
    let year: number;
    if (parts[0].length === 4) {
        year = Number.parseInt(parts[0], 10);
        month = Number.parseInt(parts[1], 10);
    } else {
        month = Number.parseInt(parts[0], 10);
        year = Number.parseInt(parts[1], 10);
    }
    if (Number.isNaN(month) || Number.isNaN(year)) return periodKey;
    return `${String(month).padStart(2, "0")}${separator}${year}`;
}

export function formatEUR(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return "-";
    try {
        return value.toLocaleString("de-DE", {style: "currency", currency: "EUR"});
    } catch {
        return `€${value.toFixed(2)}`;
    }
}

/** Triggers a browser download for an in-memory blob (same pattern as usePayrollRun.ts's
 *  downloadPayroll / useEmployeeHandlers.ts / workEvidence.ts - no shared helper exists yet). */
function triggerBlobDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}

async function fetchPayrollRun(token: string, periodKey: string, branch: string): Promise<PayrollRunApiResponse | null> {
    try {
        const response = await fetch(`${API_BASE_URL}/api/payroll/run?branch=${branch}&periodKey=${periodKey}`, {
            method: "GET",
            headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
        });
        if (!response.ok) return null;
        return await response.json();
    } catch {
        return null;
    }
}

// ─── Hook ───────────────────────────────────────────────────────────────────

/**
 * Fat hook for the Reports page (src/components/Reports/Reports.tsx).
 *
 * Real data currently wired up from exposed backend APIs:
 *  - Period/branch selection: global PeriodContext (top toolbar / MobilePeriodBranchFilterBar).
 *  - Beneficiary count + net payroll total ("Procesimi në bankë" stats) and the bank preview
 *    rows (employee name, branch, net amount): GET /api/payroll/run - the same endpoint
 *    usePayrollRun.ts/useDashboard.ts already use, so this mirrors an established, safe-to-auto-
 *    fetch (GET, no explicit "Calculate" trigger needed - see useDashboard.ts's fetchPayrollRun)
 *    read of already-calculated payroll figures for the selected period+branch.
 *
 * Known gaps - there is no exposed backend endpoint today for:
 *  - Bank-specific export formats ("Formati sipas bankës"). Per-employee bank data itself now
 *    exists: GET /api/payroll/run returns `bankAccount` (single object, null when none) on each
 *    calculation, used for the bank preview columns and the "Banka" filter.
 *  - The 8 managerial/HR reports (cost by branch, monthly comparison, budget vs. actual, etc.) -
 *    no summary/reporting endpoints exist for any of them yet.
 *  - "Fletëpagesat" (payslips) / "Gjenero raport" actions.
 *  - ATK preview (no backend preview endpoint exists yet - only the export is wired up, see
 *    `exportAtkReport` below).
 * All of the above stay disabled with a "not available yet" tooltip in the UI until a real
 * endpoint exists - see the PR/task description for the full list handed back to the user.
 *
 * The ATK export (GET /api/payroll/run/export/atk) IS wired up: it is gated server-side on the
 * selected period's workflow stage being FINALIZED (see `allPeriods`/`AllPayrollPeriod.workflowStage`
 * from `PeriodContext`, already shared with the top toolbar's period selector - no duplicate
 * fetch here), and surfaces a 409 "not finalized yet" response as a user-facing error message.
 */
export function useReports() {
    const {token} = useAuth();
    const {selectedPeriod, selectedPeriodValue, dashboardBranch, allPeriods, refreshPeriods} = usePeriod();
    const selectedBranch = dashboardBranch;

    const isPeriodLocked = selectedPeriodValue?.toUpperCase().includes("LOCKED") ?? false;

    // Re-fetch periods (incl. workflowStage) whenever the Reports page mounts, so a workflow
    // transition (e.g. approve -> finalize) made elsewhere is reflected here without requiring a
    // full page reload.
    React.useEffect(() => {
        refreshPeriods();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Workflow stage (OPEN | CALCULATED | APPROVED | FINALIZED) of the currently selected period,
    // read from the same `allPeriods` list the top toolbar's period selector already fetches.
    const atkWorkflowStage = React.useMemo(
        () => allPeriods.find((p) => p.periodKey === selectedPeriod)?.workflowStage,
        [allPeriods, selectedPeriod],
    );
    const isAtkExportReady = atkWorkflowStage === "FINALIZED";

    const [payrollRun, setPayrollRun] = React.useState<PayrollRunApiResponse | null>(null);
    const [payrollRunLoading, setPayrollRunLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (!token || !selectedPeriod) {
            setPayrollRun(null);
            return;
        }
        let cancelled = false;
        setPayrollRunLoading(true);
        fetchPayrollRun(token, selectedPeriod, selectedBranch).then((data) => {
            if (!cancelled) {
                setPayrollRun(data);
                setPayrollRunLoading(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [token, selectedPeriod, selectedBranch]);

    const beneficiaryCount = payrollRun?.payrollCalculations.length ?? 0;
    const totalNetAmount = payrollRun?.totalNetPay ?? 0;
    const financialsAvailable = payrollRun !== null;

    const beneficiaryRows: ReportsBeneficiaryRow[] = React.useMemo(
        () =>
            (payrollRun?.payrollCalculations ?? []).map((c) => ({
                id: c.employeeId ?? c.contractId ?? c.employeeNumber,
                name: c.name,
                branch: c.branch,
                netAmount: c.bankNet ?? c.netPay ?? 0,
                bankAccount: c.bankAccount ?? null,
            })),
        [payrollRun],
    );

    // "Banka" filter: distinct bank names from the loaded rows ("ALL" = no filter).
    const [bankFilter, setBankFilter] = React.useState<string>("ALL");
    const bankOptions: string[] = React.useMemo(
        () =>
            Array.from(
                new Set(beneficiaryRows.map((r) => r.bankAccount?.bankName?.trim()).filter((n): n is string => !!n)),
            ).sort((a, b) => a.localeCompare(b)),
        [beneficiaryRows],
    );
    // A previously chosen bank may vanish after a period/branch change: fall back to "all".
    const activeBankFilter = bankFilter === "ALL" || bankOptions.includes(bankFilter) ? bankFilter : "ALL";
    const filteredBeneficiaryRows: ReportsBeneficiaryRow[] = React.useMemo(
        () =>
            activeBankFilter === "ALL"
                ? beneficiaryRows
                : beneficiaryRows.filter((r) => r.bankAccount?.bankName?.trim() === activeBankFilter),
        [beneficiaryRows, activeBankFilter],
    );

    // ─── Bank preview: reveal toggle + client-side pagination ──────────────────────────────────────
    const [showAccounts, setShowAccounts] = React.useState<boolean>(false);
    const [bankPage, setBankPage] = React.useState<number>(0);
    const [bankRowsPerPage, setBankRowsPerPage] = React.useState<number>(10);

    // Never keep account numbers revealed (or a stale page) across a period/branch change.
    React.useEffect(() => {
        setShowAccounts(false);
        setBankPage(0);
    }, [selectedPeriod, selectedBranch]);

    React.useEffect(() => {
        setBankPage(0);
    }, [activeBankFilter]);

    // Clamp when rows shrink (e.g. new data after the page was chosen).
    const maxBankPage = Math.max(0, Math.ceil(filteredBeneficiaryRows.length / bankRowsPerPage) - 1);
    const currentBankPage = Math.min(bankPage, maxBankPage);
    const pagedBeneficiaryRows: ReportsBeneficiaryRow[] = React.useMemo(
        () => filteredBeneficiaryRows.slice(currentBankPage * bankRowsPerPage, (currentBankPage + 1) * bankRowsPerPage),
        [filteredBeneficiaryRows, currentBankPage, bankRowsPerPage],
    );
    const handleBankPageChange = (_event: unknown, page: number): void => setBankPage(page);
    const handleBankRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setBankRowsPerPage(parseInt(event.target.value, 10));
        setBankPage(0);
    };
    const toggleShowAccounts = (): void => setShowAccounts((prev) => !prev);

    // ─── UI-only state (collapsible sections, toggles) - kept here per the "fat hook" convention:
    // the page component just destructures and renders. ──────────────────────────────────────────
    const [atkExpanded, setAtkExpanded] = React.useState<boolean>(true);
    const [atkFieldsExpanded, setAtkFieldsExpanded] = React.useState<boolean>(false);
    const [managerialExpanded, setManagerialExpanded] = React.useState<boolean>(true);
    const [moreMenuAnchor, setMoreMenuAnchor] = React.useState<null | HTMLElement>(null);

    const atkFilename = formatPeriodNumeric(selectedPeriod, "-");
    const paySlipPeriodLabel = formatPeriodNumeric(selectedPeriod, "/");

    // ─── ATK export (GET /api/payroll/run/export/atk) ──────────────────────────────────────────
    // Gated server-side on workflowStage === 'FINALIZED' (isAtkExportReady above); the button in
    // Reports.tsx disables itself using that flag and only calls this when enabled, but the 409
    // branch here stays as a defensive backstop (e.g. a stale selection, or the period changed
    // between render and click).
    const [atkExporting, setAtkExporting] = React.useState<boolean>(false);
    const [atkExportError, setAtkExportError] = React.useState<string>("");

    const exportAtkReport = React.useCallback(async (): Promise<void> => {
        if (!token || !selectedPeriod) return;
        setAtkExportError("");
        setAtkExporting(true);
        try {
            const branchParam = selectedBranch === ALL_BRANCHES_VALUE ? "ALL" : selectedBranch;
            const {blob, filename} = await exportAtkReportRequest(token, selectedPeriod, branchParam);
            triggerBlobDownload(blob, filename);
        } catch (error) {
            if (error instanceof PayrollWorkflowConflictError) {
                setAtkExportError(error.message);
            } else {
                setAtkExportError(error instanceof Error ? error.message : "Failed to export ATK report");
            }
        } finally {
            setAtkExporting(false);
        }
    }, [token, selectedPeriod, selectedBranch]);

    const clearAtkExportError = React.useCallback((): void => setAtkExportError(""), []);

    // ─── Bank list export (GET /api/payroll/bank-export) ──────────────────────────────────────
    // Supported banks (those with a template) are fetched once. The bank filter holds bank NAMES,
    // so the selected bank is matched by name (case-insensitive, trimmed) against `bankName`.
    const [supportedBanks, setSupportedBanks] = React.useState<SupportedBankExport[]>([]);
    React.useEffect(() => {
        if (!token) return;
        let cancelled = false;
        fetchSupportedBankExports(token)
            .then((list) => {
                if (!cancelled) setSupportedBanks(list);
            })
            .catch(() => {
                if (!cancelled) setSupportedBanks([]);
            });
        return () => {
            cancelled = true;
        };
    }, [token]);

    const selectedSupportedBank: SupportedBankExport | null = React.useMemo(() => {
        if (activeBankFilter === "ALL") return null;
        const wanted = activeBankFilter.trim().toLowerCase();
        return supportedBanks.find((b) => b.bankName?.trim().toLowerCase() === wanted) ?? null;
    }, [supportedBanks, activeBankFilter]);

    // Why the "Export list" button is disabled ("none" = enabled).
    const bankExportDisabledReason: "none" | "periodNotFinalized" | "selectBank" | "noTemplate" =
        !isAtkExportReady
            ? "periodNotFinalized"
            : activeBankFilter === "ALL"
                ? "selectBank"
                : !selectedSupportedBank
                    ? "noTemplate"
                    : "none";

    const [bankExporting, setBankExporting] = React.useState<boolean>(false);
    /** null = no error; "" = failed without a backend message (UI shows a generic i18n text). */
    const [bankExportError, setBankExportError] = React.useState<string | null>(null);

    const exportBankListFile = React.useCallback(async (): Promise<void> => {
        // Guard (in addition to the disabled button): never call the API for ALL / no template / not finalized.
        if (!token || !selectedPeriod || bankExportDisabledReason !== "none" || !selectedSupportedBank) return;
        setBankExportError(null);
        setBankExporting(true);
        try {
            const branchParam = selectedBranch === ALL_BRANCHES_VALUE ? "ALL" : selectedBranch;
            const ext = /xlsx|xls|csv/i.test(selectedSupportedBank.format) ? selectedSupportedBank.format.toLowerCase() : "xlsx";
            const {blob, filename} = await exportBankListRequest(
                token,
                selectedPeriod,
                branchParam,
                {bankId: selectedSupportedBank.bankId, bankName: selectedSupportedBank.bankName},
                `${selectedSupportedBank.code}-${selectedPeriod}.${ext}`,
            );
            triggerBlobDownload(blob, filename);
        } catch (error) {
            setBankExportError(error instanceof BankExportError ? error.message : "");
        } finally {
            setBankExporting(false);
        }
    }, [token, selectedPeriod, selectedBranch, bankExportDisabledReason, selectedSupportedBank]);

    const clearBankExportError = React.useCallback((): void => setBankExportError(null), []);

    return {
        selectedPeriod,
        selectedBranch,
        isPeriodLocked,
        payrollRunLoading,
        financialsAvailable,
        beneficiaryCount,
        totalNetAmount,
        beneficiaryRows,
        filteredBeneficiaryRows,
        pagedBeneficiaryRows,
        bankPage: currentBankPage,
        bankRowsPerPage,
        handleBankPageChange,
        handleBankRowsPerPageChange,
        showAccounts,
        toggleShowAccounts,
        bankOptions,
        bankFilter: activeBankFilter,
        setBankFilter,
        atkFilename,
        paySlipPeriodLabel,
        atkExpanded,
        setAtkExpanded,
        atkFieldsExpanded,
        setAtkFieldsExpanded,
        managerialExpanded,
        setManagerialExpanded,
        moreMenuAnchor,
        setMoreMenuAnchor,
        atkWorkflowStage,
        isAtkExportReady,
        atkExporting,
        atkExportError,
        exportAtkReport,
        clearAtkExportError,
        bankExportDisabledReason,
        bankExporting,
        bankExportError,
        exportBankListFile,
        clearBankExportError,
    };
}
